/**
 * RedeemInviteForm — public form to redeem an invite code.
 *
 * Flow:
 *   1. User enters invite code (BDAT-XXXX-XXXX-XXXX) + their email
 *   2. POST /auth/redeem-invite
 *   3. If valid → user is now approved; show "Login with Google" CTA
 */
import React, { useState } from 'react';
import { AuthService } from '../services/authService';

interface RedeemInviteFormProps {
  onApproved: () => void;
  onSwitchToRequest: () => void;
  onBack: () => void;
}

export default function RedeemInviteForm({ onApproved, onSwitchToRequest, onBack }: RedeemInviteFormProps) {
  const [code, setCode] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState<{ email: string } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim() || !email.trim()) {
      setError('Vul zowel de code als je e-mail in.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const r = await AuthService.redeemInvite(code.trim(), email.trim());
      setSuccess({ email: r.email });
    } catch (err: any) {
      setError(err?.message || 'Code inwisselen mislukt');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="space-y-5">
        <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-5">
          <p className="text-[10px] font-black uppercase tracking-[0.3em] text-emerald-300">Code geldig!</p>
          <p className="mt-3 text-base text-white leading-relaxed">
            Je e-mail <span className="font-mono text-emerald-300">{success.email}</span> is nu goedgekeurd.
            Log in met Google om toegang te krijgen.
          </p>
        </div>
        <button
          type="button"
          onClick={onApproved}
          className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-bold uppercase tracking-[0.18em] text-white transition hover:border-white/25 hover:bg-white/10"
        >
          Naar login →
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400">Uitnodigingscode</label>
        <input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          required
          autoComplete="off"
          className="mt-2 w-full rounded-2xl border border-white/10 bg-[#0b1120] px-4 py-3 font-mono text-sm tracking-wider text-white placeholder-slate-500 outline-none focus:border-indigo-400"
          placeholder="BDAT-XXXX-XXXX-XXXX"
        />
      </div>
      <div>
        <label className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400">Jouw e-mail</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="mt-2 w-full rounded-2xl border border-white/10 bg-[#0b1120] px-4 py-3 text-sm text-white placeholder-slate-500 outline-none focus:border-indigo-400"
          placeholder="jan@bedrijf.nl"
        />
        <p className="mt-2 text-xs text-slate-500">
          De code is gekoppeld aan dit e-mailadres.
        </p>
      </div>

      {error && (
        <div className="rounded-2xl border border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-200">{error}</div>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="w-full rounded-2xl bg-gradient-to-r from-indigo-500 to-violet-500 px-4 py-3 text-sm font-black uppercase tracking-[0.18em] text-white transition hover:from-indigo-400 hover:to-violet-400 disabled:opacity-50"
      >
        {submitting ? 'Controleren…' : 'Code inwisselen'}
      </button>

      <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-[0.25em]">
        <button type="button" onClick={onSwitchToRequest} className="text-slate-500 hover:text-slate-300">
          Geen code? Vraag aan →
        </button>
        <button type="button" onClick={onBack} className="text-slate-500 hover:text-slate-300">
          ← Terug
        </button>
      </div>
    </form>
  );
}
